// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { withStyles } from "tss-react/mui";
import { Dialog, DialogContent, DialogActions, Card } from '@mui/material';

import CommentModel, { CommentDTO } from '@shared/models/comment';
import { getTestAttr } from '@shared/utils/common';
import { resetForm } from '@shared/utils/form';
import { COMMENT_FIELDS } from '@shared/constants/comment';
import EmptyCommentsIcon from '@shared/icons/EmptyComments';
import RefreshIcon from '@shared/icons/Refresh';
import Flex from '@shared/components/Flex';
import Button from '@shared/components/Button';
import Loading from '@shared/components/Loading';
import TextField from '@shared/components/TextField';
import DialogTitle from '@shared/components/DialogTitle';
import Message from '@shared/components/Message';
import { Id } from '@shared/types/common';
import CommentForm from './forms/comment';
import Comment, { Mode } from './components/Comment';

import './Comments.css'

import Label from '../Label';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import {DELIVERABLE_EXCEPTION_CODES} from '@shared/constants/deliverable';
import container from '@core/di';
import { DeliverablesStore, JobRequestsStore } from '@shared/stores';

// Prop interface for the main class.
export interface CommentsProps {
  className?: string;
  id: Id;
  data: Array<CommentModel>;  // Obsolete, to be remove.  Call the function to read the comment list instead.
  isDeliverable?: boolean;
  exceptionCode?: string; // set if deli.  Undefined if jobReq.
  sendComment: (id: Id, comment: Partial<CommentDTO> & {exceptionCode: string}) => Promise<any>;
  getComments: (id: Id) => Promise<any>;  // Obsolete, to be remove.  Call CommentStore directly.
  resetComments: () => any;
  close: () => any;
  onSetFlagNewComment?: () => any;
}

// Class showing the popup dialog to display and add comments for a delivarable/jobReq.
@observer
class Comments extends React.Component<CommentsProps, {dataSource: Array<CommentModel>}> {
  // Stateful vars that can trigger rerendering.
  @observable loading = false;
  @observable isSubmitting = false;
  @observable selectedCode: string;

  private jobRequestsStore = container.get<JobRequestsStore>(JobRequestsStore.diToken);
  private deliverablesStore = container.get<DeliverablesStore>(DeliverablesStore.diToken);
  
  private form;
  private commentsRef = React.createRef<HTMLDivElement>();

  // Constructor.
  constructor(props: CommentsProps) {
    super(props);

    this.form = new CommentForm({
      hooks: {
        onSuccess: this.handleSubmit
      }
    });
    
    this.selectedCode = props.exceptionCode ?? '';

    // Store data in state to rerender the comment list after adding a new comment.
    this.state = {dataSource: props.data};
    makeObservable(this);
  }

  componentDidMount(): void {
    this.refresh();
  }

  // Scroll to the last comment after updating content.
  componentDidUpdate(prevProps: CommentsProps) {
    const { dataSource } = this.state;

    if (prevProps.data.length !== dataSource.length) {
      this.scrollToBottom();
    }
  }

  // bubble up request to clear the stored comment list when exits.
  componentWillUnmount() {
    const { resetComments } = this.props;

    resetComments();
  }

  // Reload comments from portal-api.
  @action reloadComments = async () => {
    const { id, isDeliverable } = this.props;

    this.loading = true;

    try {
      if(isDeliverable) { // Reload deliverable comments.
        await this.deliverablesStore.commentsStore.getList(id); // Update class list.
        const list = this.deliverablesStore.commentsStore.list; // Read the new list.
        runInAction(() => {
          this.loading = false;
        });
        console.log("*** Reload Deli Comments:", list.slice());
        return list;
      }
      else { // Reload jobRequest comments.
        await this.jobRequestsStore.commentsStore.getList(id);  // Update class list.
        const list = this.jobRequestsStore.commentsStore.list;  // Read the new list.
        runInAction(() => {
          this.loading = false;
        });
        console.log("*** Reload JobReq Comments:", list.slice());
        return list;
      }
    } 
    catch(err) {
      console.log("****** Exception in Comment reloadComments:", err);
      runInAction(() => {
        this.loading = false;
      });
      return [];
    }
  }

  // Send request to save a new comment and/or exception code. 
  private handleSubmit = async (form) => {
    const { id, exceptionCode, sendComment, onSetFlagNewComment } = this.props;

    console.log("*** submit form:", form.values());
    const comment: Partial<CommentDTO> = {...form.values()};

    // If comment is blank, insert a message about the switch in the exception code such as "None -> Warning".
    if((typeof comment.body !== "string" || comment.body.length === 0) && exceptionCode != undefined) {
      comment.body = `${exceptionCode ? exceptionCode : DELIVERABLE_EXCEPTION_CODES.none} ➜ ${this.selectedCode}`;
    }

    runInAction(() => {
      this.isSubmitting = true;
    });

    try {
      await sendComment(id, {...comment, ...{exceptionCode: this.selectedCode}}); // Combine msg txt and exception code, and submit.

      // Clear the form values.
      resetForm(this.form);
      this.form.$(COMMENT_FIELDS.body).set('rules', 'required'); // reset rules='required'.

      // Reload the comment list.
      const newComments = await this.reloadComments();
      console.log("*** reload comment list after send:", newComments);

      // Set state to trigger a rerender to display the new comment list.
      this.setState({dataSource: newComments});

      if (onSetFlagNewComment)
        onSetFlagNewComment();

      runInAction(() => {
        this.isSubmitting = false;
      });
    } 
    catch {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  // Croll to the bottom of the comment list.
  private scrollToBottom = () => {
    if (this.commentsRef.current) {
      this.commentsRef.current.scrollTop = this.commentsRef.current.scrollHeight;
    }
  }

  // Reload the comment list.
  private refresh = async () => {
    // load list of comments.
    const newComments = await this.reloadComments();
    // Set state to trigger a rerender to display the new comment list.
    this.setState({dataSource: newComments});
  }

  // Set exceptionCode to the selected value.  Also configure form to allow submitting without a message.
  @action setExceptionCode =  (id: string, event: React.MouseEvent) => {
    const { exceptionCode } = this.props;
    event.preventDefault(); // skip default behavior which is submitting form.

    // Remove 'active' style from the current exceptioncode button.
    // const btnElem = document.getElementById(this.curException == undefined ? 'undefined' : this.curException);
    // if(btnElem != undefined) {
    //   console.log("********** remove e-active from: ", btnElem);
    //   btnElem.classList.remove('e-active');
    // }

    if(this.selectedCode != id) { // if click on a different code, switch.
      this.selectedCode = id;
    }
    else { // if click on the same code, clear. Note: this will clear all codes not just the selected one.
      this.selectedCode = DELIVERABLE_EXCEPTION_CODES.none;
    }

    if(this.selectedCode != exceptionCode) {
      this.form.$(COMMENT_FIELDS.body).set('rules', ''); // clear 'rules' if exceptionCode has been changed to allow submitting without a msg.
    }
    else {
      this.form.$(COMMENT_FIELDS.body).set('rules', 'required'); // Otherwise, set 'rules' to require a msg string.
    }
  }

  // Build the 'main' content (the section showing the comment list) of the dialog.
  private get content() {
    const classes = withStyles.getClasses(this.props);
    const { dataSource } = this.state;

    //console.log("*** dataSource:", dataSource.slice());

    // Show the spinwheel while loading.
    if (this.loading && !dataSource.length) {
      return (
        <Loading classes={{ root: classes.loading }} />
      );
    }

    // Show blank list.
    if (!dataSource.length) {
      return (
        <Card
          classes={{ root: classes.noCommentsWrapper }}
        >
          <Message
            icon={<EmptyCommentsIcon />}
            heading="There are no comments yet."
            subheading="To write a comment, please use the form below"
            classes={{ root: classes.noComments }}
          />
        </Card>
      );
    }

    // List the comments.
    return (
      <div ref={this.commentsRef} className={classes.commentsList}>
        {dataSource.map((comment) => (
          <Comment
            key={comment.createdDate}
            mode={Mode.view}
            data={comment}
            classes={{ root: classes.comment }}
          />
        ))}
      </div>
    );
  }

  render() {
    const { isDeliverable, close } = this.props;
    const classes = withStyles.getClasses(this.props);
    const { dataSource } = this.state;
    // Set the square (checked/unchecked) for the exception code buttons.
    let warningIcon = 'e-btngrp-yellow';
    let moderateIcon = 'e-btngrp-orange';
    let severeIcon = 'e-btngrp-red';

    if(this.selectedCode == DELIVERABLE_EXCEPTION_CODES.warning) {
      warningIcon = 'e-btngrp-yellow-on';
      //this.onCreatedBtn('warning');
    }
    else if(this.selectedCode == DELIVERABLE_EXCEPTION_CODES.moderate) {
      moderateIcon = 'e-btngrp-orange-on';
      //this.onCreatedBtn('moderate');
    }
    else if(this.selectedCode == DELIVERABLE_EXCEPTION_CODES.severe) {
      severeIcon = 'e-btngrp-red-on';
      //this.onCreatedBtn('severe');
    }

    // console.log("*** in Comments render:", exceptionCode, ",", noneIcon, ",", warningIcon, ",", moderateIcon, ",", severeIcon);

    return (
      <Dialog
        open
        classes={{ paper: classes.root }}
      >
        {/* header section with header, "Refresh" button, and "X" button. */}
        <DialogTitle 
          classes={{ root: classes.title }}
          heading={(
            <Flex alignItems="center" >
              <h1 className={classes.heading}>Comments</h1>
              <Button
                {...getTestAttr('Comment', 'refresh-comments', 'btn')}
                disableRipple
                className={classes.refreshBtn}
                text={(
                  <Flex alignItems="center">
                    <RefreshIcon classes={{ root: classes.refreshIcon }} />
                    <span className={classes.refreshText}>Refresh</span>
                  </Flex>
                )}
                variant="text"
                size="small"
                loading={this.loading && Boolean(dataSource.length)}
                onClick={this.refresh}
              />
            </Flex>
          )}
          onClose={close}
        />

        {/* 'Main' section with the comment list. */}
        <DialogContent className={classes.content}>
          {this.content}
        </DialogContent>

        {/* footer section with the Exception code checkboxes, new comment text field, and 'Send' button */}
        <DialogActions className={classes.actions}>
          <form onSubmit={this.form.onSubmit} className={classes.form}>
            { isDeliverable && // Show exception selection if deliverable and skip if jobReq.
            <Flex>
              <Label
                big 
                text="Exception code"
                classes={{root: classes.selectColor}} 
              />
              <Flex id="bgicon" className='button-group-section'>
                <ButtonComponent id={DELIVERABLE_EXCEPTION_CODES.warning}  iconCss={warningIcon}  className='button-group-btn' onClick={(e) => this.setExceptionCode(DELIVERABLE_EXCEPTION_CODES.warning, e)}>{DELIVERABLE_EXCEPTION_CODES.warning}</ButtonComponent>
                <ButtonComponent id={DELIVERABLE_EXCEPTION_CODES.moderate} iconCss={moderateIcon} className='button-group-btn' onClick={(e) => this.setExceptionCode(DELIVERABLE_EXCEPTION_CODES.moderate, e)}>{DELIVERABLE_EXCEPTION_CODES.moderate}</ButtonComponent>
                <ButtonComponent id={DELIVERABLE_EXCEPTION_CODES.severe}   iconCss={severeIcon}   className='button-group-btn' onClick={(e) => this.setExceptionCode(DELIVERABLE_EXCEPTION_CODES.severe, e)} >{DELIVERABLE_EXCEPTION_CODES.severe}</ButtonComponent>
              </Flex>
            </Flex>
            }
            <Flex
              autoWidth={false}
              alignItems="center"
              wrap="nowrap"
              className={classes.actionGroup}
            >
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Write a comment"
                margin="none"
                field={this.form.$(COMMENT_FIELDS.body)}
                testAttributeArgs={{
                  module: 'Comment',
                  elementDescription: 'comment',
                }}
              />
              <Button
                {...getTestAttr('Comment', 'send-comment', 'btn')}
                className={classes.actionBtn}
                type="submit"
                text="Send"
                loading={this.isSubmitting}
              />
            </Flex>
            
          </form>
        </DialogActions>
      </Dialog >
    );
  }
}

const CommentsStyled = withStyles(
  Comments, 
  (theme, props) => ({
    "root": {
      width: 666,
      maxWidth: 'none',
      margin: 0,
      border: 'none',
    },
    "loading": {
      height: 235,
    },
    "title": {
      borderBottom: `1px solid ${theme.palette.colors.greyLight}`,
    },
    "heading": {
      fontSize: 20,
      fontWeight: 500,
    },
    "closeIcon": {
      padding: 0,
    },
    "noCommentsWrapper": {
      border: 'none',
      backgroundColor: theme.palette.colors.white,
      height: 235,
    },
    "noComments": {
      height: '100%',
    },
    "content": {
      padding: theme.spacing(0, 6),
      fontSize: 15,
      minHeight: 235,
    },
    "selectColor": { },
    "commentsList": {
      maxHeight: 300,
      overflowY: 'auto',
      padding: theme.spacing(6, 1, 6, 0),
    },
    "actions": {
      backgroundColor: theme.palette.colors.greyLight5,
      padding: theme.spacing(6),
      borderTop: `1px solid ${theme.palette.colors.greyLight}`,
    },
    "actionGroup": {
      gap: theme.spacing(5),
    },
    "actionBtn": {
      fontSize: 16,
      marginLeft: theme.spacing(4),
    },
    "refreshBtn": {
      fontSize: 14,
      marginLeft: theme.spacing(),
    },
    "refreshIcon": {
      fontSize: 24,
      marginRight: theme.spacing(),
      marginLeft: -theme.spacing(),
    },
    "refreshText": {
      ...theme.shortcuts.borderPrimaryDashed,
    },
    "comment": {
      marginBottom: theme.spacing(2),

      '&:last-of-type': {
        marginBottom: 0
      }
    },
    "form": {
      width: '100%',
    }
  })
);

export default CommentsStyled;